import { Component, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { RouterReducerState } from '@ngrx/router-store';
import { Observable, Subject, BehaviorSubject, Subscription } from 'rxjs';
import { withLatestFrom, distinctUntilChanged, take } from 'rxjs/operators';
import { ExportService } from '../../../services/export.service';
import { ALL_ORDERS, MONITOR, REPORTS } from '../../../gameday.constants';
import { ReportType, ReportTypes } from '../../../models/report';

const FULL_SELECTED_REPORTS = [
  ReportType.AllOrders,
  ReportType.AccountsReport,
  ReportType.ManagersReport,
  ReportType.OrderMonitor
];
const GAME_DAY_URL_SEGMENT = 'gameday';
const ALL_ORDERS_PATH = new RegExp(`/${GAME_DAY_URL_SEGMENT}/${ALL_ORDERS}`);
const MONITOR_PATH = new RegExp(`/${GAME_DAY_URL_SEGMENT}/${MONITOR}`);
const ACCOUNT_PATH = new RegExp(
  `/${GAME_DAY_URL_SEGMENT}/${REPORTS}.*REPORTS_SET_ACTIVE_TAB=2$`
);
const MANAGERS_REPORT_PATH = new RegExp(
  `/${GAME_DAY_URL_SEGMENT}/${REPORTS}.*REPORTS_SET_ACTIVE_TAB=[0,1]$`
);
const REPORTS_PAGES_OBJECT = Object.entries({
  [ReportType.AllOrders]: ALL_ORDERS_PATH,
  [ReportType.OrderMonitor]: MONITOR_PATH,
  [ReportType.AccountsReport]: ACCOUNT_PATH,
  [ReportType.ManagersReport]: MANAGERS_REPORT_PATH
});
const REPORT_PAGES: Map<ReportType, RegExp> = new Map<ReportType, RegExp>(
  <any>REPORTS_PAGES_OBJECT
);

@Component({
  selector: 'muni-gameday-export-report-modal',
  templateUrl: './export-report-modal.template.html',
  styleUrls: ['./export-report-modal.styles.scss']
})
export class ExportReportModalComponent implements OnDestroy {
  public response: Subject<any>;
  public readonly reportType: any = ReportType;
  public readonly isError: Subject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  public readonly hasAccountsPermissions: Observable<boolean> = this
    .exportService.hasAccountsPermissions$;

  public readonly isError$: Observable<boolean> = this.isError.pipe(
    distinctUntilChanged()
  );

  public readonly routerPathWithPermissions$: Observable<
    [string, boolean]
  > = this.store.pipe(
    select(
      ({
        router: {
          state: { url }
        }
      }: {
        router: RouterReducerState;
      }) => url
    ),
    withLatestFrom(this.hasAccountsPermissions),
    distinctUntilChanged(),
    take(1)
  );

  private isAccountsActive: boolean;
  private reportsToExport: ReportTypes = [];

  private readonly pathSubscription: Subscription = this.routerPathWithPermissions$.subscribe(
    ([path, hasAccountsPermissions]) => {
      REPORT_PAGES.forEach((pagePath, reportType) => {
        if (pagePath.test(path)) {
          if (
            reportType !== ReportType.AccountsReport ||
            (reportType === ReportType.AccountsReport && hasAccountsPermissions)
          ) {
            this.reportsToExport.push(reportType);
          }
        }
      });
    }
  );
  private readonly permissionsSubscription: Subscription = this.hasAccountsPermissions.subscribe(
    (data) => (this.isAccountsActive = data)
  );

  constructor(
    private exportService: ExportService,
    private store: Store<{ router: RouterReducerState }>
  ) {}

  public ngOnDestroy(): void {
    this.pathSubscription.unsubscribe();
    this.permissionsSubscription.unsubscribe();
  }

  public closeModal(): void {
    this.response.complete();
  }

  public isAllSelected(): boolean {
    return this.isAccountsActive
      ? this.reportsToExport.length === FULL_SELECTED_REPORTS.length
      : this.reportsToExport.length === FULL_SELECTED_REPORTS.length - 1;
  }

  public toggleAll(event: Event): void {
    if ((event.target as HTMLInputElement).checked) {
      this.setErrorState(false);
      this.reportsToExport = [...FULL_SELECTED_REPORTS].filter(
        (report) =>
          report !== ReportType.AccountsReport ||
          (report === ReportType.AccountsReport && this.isAccountsActive)
      );
    } else {
      this.reportsToExport = [];
    }
  }

  public toggleReportInExport(report: ReportType): void {
    const reportIndex: number = this.reportsToExport.findIndex(
      (r) => r === report
    );

    if (reportIndex < 0) {
      this.reportsToExport.push(report);
      this.setErrorState(false);
    } else {
      this.reportsToExport.splice(reportIndex, 1);
    }
  }

  public getCheckboxValue(report: ReportType): boolean {
    return this.reportsToExport.some((r) => r === report);
  }

  public setErrorState(errorState: boolean): void {
    this.isError.next(errorState);
  }

  public exportReports(): void {
    if (this.reportsToExport.length === 0) {
      this.setErrorState(true);
      return;
    }

    this.exportService.export(this.reportsToExport);
    this.closeModal();
  }
}
